import { useState, type FC } from "react";
import Modal from "../Modal";
import { useTranslation } from "react-i18next";
import { theme } from "../../../theme";
import Body from "../../atoms/typography/Body";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Alert from "../Alert";
import { useCreateMonitorEventComment } from "../../../graphql/hooks/aml/useCreateMonitorEventComments";
import BodySmall from "../../atoms/typography/BodySmall";
import dayjs from "dayjs";
import type {
  Parameter,
  ParameterExclusionOption,
} from "../../../models/AmlParameter";
import Divider from "../../atoms/Divider";
import TextField from "../../atoms/inputs/TextField";
import ActionButton from "../../atoms/buttons/ActionButton";
import { CommentIcon } from "../../atoms/icons/CommentIcon";
import { CheckmarkIconSmall } from "../../atoms/icons/CheckmarkIcons";
import type { CommentModel } from "../../../models/CommentModel";
import Small from "../../atoms/typography/Small";
import AmlMonitorException from "./AmlMonitorException";
import { useMarkMonitorEventAsHandled } from "../../../graphql/hooks/aml/useMarkMonitorEventAsHandled";
import { Row } from "react-grid-system";
import ChevronButton from "../../atoms/buttons/ChevronButtons";

interface Props {
  clientIdentifier: string;
  clientName: string;
  monitorEventId: string;
  eventTitle: string;
  eventDate: Date;
  descriptionLines: string[];
  parameters: Array<Parameter>;
  parameterExclusionOptions?: ParameterExclusionOption;
  comments?: Array<CommentModel>;
  refetch?: () => void;
  onClose: () => void;
  onHandled?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

const HandleMonitorEventModal: FC<Props> = ({
  clientIdentifier,
  clientName,
  monitorEventId,
  eventTitle,
  eventDate,
  descriptionLines,
  parameters,
  parameterExclusionOptions,
  comments,
  refetch,
  onClose,
  onHandled,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [comment, setComment] = useState("");

  const onCompletedLeaveComment = (result: any) => {
    if (result?.aml?.createMonitorEventComment.success) {
      setComment("");
      setSuccessMessage(t("aml.commentAddedSuccessfully"));
      setErrorMessage("");
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } else {
      setErrorMessage(t("aml.commentCouldNotBeAdded"));
      setSuccessMessage("");
    }
  };
  const onErrorLeaveComment = (e?: any) => {
    setErrorMessage(e?.toString());
    setSuccessMessage("");
  };
  const {
    createMonitorEventComment,
    error: commentError,
    loading: commentLoading,
  } = useCreateMonitorEventComment({
    onCompleted: onCompletedLeaveComment,
    onError: onErrorLeaveComment,
  });

  const onClickLeaveComment = async () => {
    try {
      await createMonitorEventComment({
        variables: {
          input: {
            clientIdentifier: clientIdentifier,
            comment: comment,
            monitoringEventId: monitorEventId,
          },
        },
      });
    } catch (error) {
      setErrorMessage(error?.toString() ?? t("aml.commentCouldNotBeAdded"));
    }
  };

  const onCompletedMarkAsHandled = (result?: any) => {
    if (result?.aml?.markMonitorEventAsHandled?.success) {
      setSuccessMessage(t("aml.theEventHasBeenMarkedAsHandled"));
      setErrorMessage("");
      setTimeout(() => {
        setSuccessMessage("");
        onHandled?.();
      }, 3000);
    } else {
      setErrorMessage(t("aml.theEventCouldNotBeMarkedAsHandled"));
      setSuccessMessage("");
    }
  };
  const onErrorMarkAsHandled = (e?: any) => {
    setErrorMessage(e?.toString() || t("errors.somethingWentWrong"));
    setSuccessMessage("");
  };

  const { markMonitorEventAsHandled, error, loading } =
    useMarkMonitorEventAsHandled({
      onCompleted: onCompletedMarkAsHandled,
      onError: onErrorMarkAsHandled,
    });

  const onClickMarkAsHandled = async () => {
    try {
      await markMonitorEventAsHandled({
        variables: {
          input: {
            clientIdentifier: clientIdentifier,
            comment: comment,
            monitoringEventId: monitorEventId,
          },
        },
      });
    } catch (error) {
      console.error(error);
      setErrorMessage(
        error?.toString() || t("aml.theEventCouldNotBeMarkedAsHandled"),
      );
    }
  };

  return (
    <Modal
      onClose={onClose}
      closeIcon
      title={t("aml.handleMonitoringEvent")}
      width={540}
    >
      <Body fontweight="600">
        {t("common.client")}: {clientName}
      </Body>
      <BodySmall>{clientIdentifier}</BodySmall>
      <HorizontalSpacer spacing={1} />
      <Body fontweight="600">{eventTitle}</Body>
      <BodySmall>{dayjs(eventDate).format("YYYY-MM-DD")}</BodySmall>
      <HorizontalSpacer spacing={1} />
      {descriptionLines.map((line, index) => (
        <BodySmall key={index}>{line}</BodySmall>
      ))}
      <HorizontalSpacer spacing={1} />
      {parameters.map((param) => (
        <div
          key={param.id}
          style={{
            padding: 16,
            borderRadius: 12,
            border: `1px solid ${theme.colors.grey05}`,
            margin: 8,
          }}
        >
          <BodySmall fontweight={600}>{param.title}</BodySmall>

          {parameterExclusionOptions && (
            <AmlMonitorException
              parameter={param}
              parameterExclusionOptions={parameterExclusionOptions}
              monitoringEventId={monitorEventId}
              clientIdentifier={clientIdentifier}
              onCloseModal={onClose}
              refetch={refetch}
            />
          )}
        </div>
      ))}
      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={1} />
      <TextField
        value={comment}
        onChange={setComment}
        placeholder={t("aml.comment")}
        fullWidth
      />
      <HorizontalSpacer spacing={1} />
      <div style={{ display: "flex", gap: 32, justifyContent: "flex-end" }}>
        <ActionButton
          color={theme.colors.primary}
          onClick={onClickLeaveComment}
          disabled={comment.length < 1 || successMessage.length > 0}
          title={t("aml.leaveComment")}
          loading={commentLoading}
          icon={<CommentIcon color={theme.colors.white} />}
        />
        <ActionButton
          color={theme.colors.primary}
          onClick={onClickMarkAsHandled}
          disabled={comment.length < 1 || successMessage.length > 0}
          title={t("aml.saveAndMarkAsHandled")}
          loading={loading}
          icon={<CheckmarkIconSmall color={theme.colors.white} />}
        />
      </div>
      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={1} />
      <Row nogutter justify="between" align="center">
        <ChevronButton
          type="left"
          onClick={onPrev ?? (() => {})}
          disabled={!hasPrev}
        />
        <ChevronButton
          type="right"
          onClick={onNext ?? (() => {})}
          disabled={!hasNext}
        />
      </Row>
      <HorizontalSpacer spacing={1} />
      <Body fontweight="600">{t("aml.commentsAndHistory")}</Body>
      <HorizontalSpacer spacing={1} />
      {comments?.map((c) => (
        <div key={c.id}>
          <Small>
            {dayjs(c.createdAt).format("YYYY-MM-DD HH:mm")} -{" "}
            {c.createdByUser?.firstName} {c.createdByUser?.lastName}
          </Small>
          <BodySmall>{c.content}</BodySmall>
          <HorizontalSpacer spacing={1} />
        </div>
      ))}
      {successMessage && <Alert type="success" title={successMessage} />}
      {(errorMessage || commentError || error) && (
        <Alert
          type="error"
          title={t("errors.somethingWentWrong")}
          message={
            errorMessage ?? commentError?.toString() ?? error?.toString()
          }
        />
      )}
    </Modal>
  );
};

export default HandleMonitorEventModal;
